import { EventStateMachine } from './event-state-machine';
import { KeyboardManager } from './keyboard-manager';
import { registerDefaultTools } from './register-default-tools';
import type { ContainerProxy, InteractionEvent, ToolActivatedPayload } from './types';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import { ToolMode, getModifierFromEvent, mapButtonToMouseButton } from '../tools/enums';
import { DistanceMeasurementTool } from '../tools/measurement/distance-tool';
import { ToolGroupManager } from '../tools/tool-group-manager';
import type { ITool } from '../tools/types';
import type { IViewport } from '../viewports';

export class InteractionService extends PubSubService {
    static readonly SERVICE_NAME = 'InteractionService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: InteractionService.SERVICE_NAME,
        factory: (context: ServiceContext) => new InteractionService(context),
    };

    readonly EVENTS = {
        TOOL_ACTIVATED: 'interaction:tool:activated',
        TOOL_DEACTIVATED: 'interaction:tool:deactivated',
        SHORTCUT_REGISTERED: 'interaction:shortcut:registered',
    } as const;

    private containerProxies = new Map<HTMLElement, ContainerProxy>();
    private isAllInteractionCatch = false;

    private readonly eventsToIntercept: Array<keyof HTMLElementEventMap> = [
        'pointerdown',
        'pointermove',
        'pointerup',
        'pointerleave',
        'wheel',
        'contextmenu',
        'keydown',
    ];

    private stateMachines = new Map<IViewport, EventStateMachine>();

    /** Stores the previously active primary tool per viewport, so it can be restored after a one-shot tool completes. */
    private previousPrimaryToolIds = new Map<string, string>();

    /** Keyboard */
    private keyboardManager = new KeyboardManager();

    /** Service context for accessing other services */
    private context: ServiceContext | null = null;

    constructor(context?: ServiceContext) {
        super();
        if (context) {
            this.context = context;
        }
        registerDefaultTools();
        this.registerDefaultShortcuts();
    }

    setContext(context: ServiceContext): void {
        this.context = context;
    }

    initDomInteractionProxy(container: HTMLElement, viewport: IViewport): () => void {
        if (!viewport) {
            console.warn('[InteractionService] Viewport is not defined');
            return () => {};
        }
        if (this.containerProxies.has(container)) {
            console.warn('[InteractionService] Container already has a proxy registered');
            return () => this.removeContainerProxy(container);
        }

        const stateMachine = new EventStateMachine();
        this.stateMachines.set(viewport, stateMachine);

        const handler = (event: Event) => {
            // Suppress the browser context menu — right-click is used for tools
            if (event.type === 'contextmenu') {
                event.preventDefault();
                return;
            }

            if (
                !(
                    event instanceof PointerEvent ||
                    event instanceof WheelEvent ||
                    event instanceof KeyboardEvent
                )
            ) {
                return;
            }

            // Process through state machine (normalization + click/drag distinction)
            const derivedEvent = stateMachine.process(event, viewport);

            // Route derived event through the priority chain
            if (derivedEvent) {
                this.routeEvent(derivedEvent, viewport);
            }
        };

        // Make the container focusable so it can receive keydown events.
        // tabIndex = -1 means focusable via JS but not via Tab key navigation.
        if (!container.hasAttribute('tabindex')) {
            container.setAttribute('tabindex', '-1');
            container.style.outline = 'none';
        }

        // Auto-focus the container on pointerdown so subsequent keydown events fire on it
        const focusHandler = () => container.focus({ preventScroll: true });
        container.addEventListener('pointerenter', focusHandler);

        this.containerProxies.set(container, { container, viewport, handler, focusHandler });

        this.eventsToIntercept.forEach((eventType) => {
            container.addEventListener(eventType, handler, { capture: true });
        });

        return () => this.removeContainerProxy(container);
    }

    /**
     * Route an interaction event through the priority chain:
     * 1. Keyboard shortcuts (global)
     * 2. ToolGroup binding resolution (per viewport, per button)
     * 3. Overlay layer (measurements, labels)
     */
    private routeEvent(event: InteractionEvent, viewport: IViewport): void {
        if (this.isAllInteractionCatch) {
            event.consume();
            return;
        }
        if (this.routeToKeyboard(event)) return;

        // ── PRIORITY 2: ToolGroup tool resolution ──
        if (this.routeToToolGroup(event, viewport)) return;
    }

    private routeToKeyboard(event: InteractionEvent): boolean {
        const consumed = this.keyboardManager.handleKeyboardEvent(event);
        if (consumed) event.consume();
        return consumed;
    }

    private routeToToolGroup(event: InteractionEvent, viewport: IViewport): boolean {
        const toolGroupManager = this.getToolGroupManager();
        if (!toolGroupManager) return false;

        const toolGroup = toolGroupManager.getToolGroupForViewport(viewport.id);
        if (!toolGroup) return false;

        // Passive tools: no bindings, but still receive events for hover/selection ──
        const passiveTools = toolGroup.getToolsWithMode(ToolMode.PASSIVE);
        for (const tool of passiveTools) {
            try {
                if (tool.handleEvent(event)) {
                    event.consume();
                    return true;
                }
            } catch (err) {
                console.error(`[InteractionService] Passive tool '${tool.id}' threw:`, err);
            }
        }

        // Resolve matching tools for this event's button + modifier
        let tools: ITool[];

        if (event.type === 'wheel') {
            tools = toolGroup.getToolsForWheelBinding();
        } else {
            const modifier = getModifierFromEvent(event);
            const button = mapButtonToMouseButton(event.button);
            tools = toolGroup.getToolsForBinding(button, modifier);
        }

        // Try each matching tool until one consumes the event.
        // This enables click/drag coexistence: e.g., CurrentLocationTool consumes
        // mouse_click while Pan consumes mouse_drag, both on Primary button.
        for (const tool of tools) {
            if (event.type === 'pointer_down' || event.type === 'wheel') {
                tool.activate(viewport);
            }

            try {
                if (tool.handleEvent(event)) {
                    event.consume();

                    // If the tool signals completion, set it to passive and restore the previous primary tool
                    if (tool.completed) {
                        this.onToolCompleted(tool, viewport);
                    }

                    return true;
                }
            } catch (err) {
                console.error(`[InteractionService] Tool '${tool.id}' threw:`, err);
            }
        }

        return false;
    }

    /**
     * Called when a tool signals completion (e.g. a measurement was placed or cancelled).
     * Sets the tool to passive mode and restores the previously active primary tool.
     */
    private onToolCompleted(tool: ITool, viewport: IViewport): void {
        const toolGroupManager = this.getToolGroupManager();
        if (!toolGroupManager) return;

        const toolGroup = toolGroupManager.getToolGroupForViewport(viewport.id);
        if (!toolGroup) return;

        // Set completed tool to passive (still receives hover events for existing overlays)
        toolGroup.setToolPassive(tool.id);

        // Restore the previous primary tool
        this.restorePreviousPrimaryTool(viewport.id);
    }

    /**
     * Restore the previously active primary tool for a viewport.
     */
    private restorePreviousPrimaryTool(viewportId: string): void {
        const previousToolId = this.previousPrimaryToolIds.get(viewportId);
        if (!previousToolId) return;

        this.previousPrimaryToolIds.delete(viewportId);

        const toolGroupManager = this.getToolGroupManager();
        if (!toolGroupManager) return;

        if (!toolGroupManager.setPrimaryToolForViewport(viewportId, previousToolId)) return;

        this.broadcast(this.EVENTS.TOOL_ACTIVATED, {
            toolId: previousToolId,
            viewportId,
        } satisfies ToolActivatedPayload);
    }

    stopAllInteraction(value: boolean): void {
        this.isAllInteractionCatch = value;
    }

    setPrimaryTool(viewportId: string, toolId: string): void {
        const toolGroupManager = this.getToolGroupManager();
        if (!toolGroupManager) return;

        // Save the drag-owning primary tool so it can be restored later.
        // Only exclusive tools qualify: CurrentLocationTool also sits on
        // Primary but never leaves it, so "restoring" it would strip the
        // camera tool instead (ESCLOUD-245).
        const currentPrimary = toolGroupManager.getPrimaryExclusiveToolForViewport(viewportId);
        if (currentPrimary && currentPrimary !== toolId) {
            this.previousPrimaryToolIds.set(viewportId, currentPrimary);
        }
        if (!toolGroupManager.setPrimaryToolForViewport(viewportId, toolId)) return;

        this.broadcast(this.EVENTS.TOOL_ACTIVATED, {
            toolId,
            viewportId,
        } satisfies ToolActivatedPayload);
    }

    /**
     * Replace the saved previous-primary tool for a viewport, if any.
     *
     * C-arm edit mode swaps the drag-owning tool while a one-shot tool may be
     * pending (ESCLOUD-245): entering the edit remaps whatever was saved to
     * the constrained tool (so a pre-edit one-shot completing mid-edit does
     * not restore a free camera tool), and leaving it remaps a saved
     * CarmRotateTool — about to be removed from the group, so unrestorable —
     * to the free rotate it stood in for.
     *
     * @param onlyIfToolId - when set, only remap if the saved entry is this tool
     */
    remapPreviousPrimaryTool(viewportId: string, toToolId: string, onlyIfToolId?: string): void {
        const saved = this.previousPrimaryToolIds.get(viewportId);
        if (saved === undefined || saved === toToolId) return;
        if (onlyIfToolId !== undefined && saved !== onlyIfToolId) return;

        this.previousPrimaryToolIds.set(viewportId, toToolId);
    }

    getKeyboardManager(): KeyboardManager {
        return this.keyboardManager;
    }

    /**
     * Get the ToolGroupManager (convenience accessor).
     */
    getToolGroupManager(): ToolGroupManager | null {
        if (!this.context) return null;
        return this.context.services.getService<ToolGroupManager>(ToolGroupManager.SERVICE_NAME);
    }

    // ── Cleanup ──

    private removeContainerProxy(container: HTMLElement): void {
        const proxy = this.containerProxies.get(container);
        if (!proxy) return;

        this.eventsToIntercept.forEach((eventType) => {
            container.removeEventListener(eventType, proxy.handler, { capture: true });
        });

        this.containerProxies.delete(container);
        const viewport = proxy.viewport;

        const sm = this.stateMachines.get(viewport);
        sm?.dispose();
        this.stateMachines.delete(viewport);

        console.log('[InteractionService] Removed proxy for container');
    }

    disposeAll(): void {
        Array.from(this.containerProxies.keys()).forEach((container) => this.removeContainerProxy(container));

        this.keyboardManager.clear();

        console.log(`[InteractionService] Disposed all container proxies`);
    }

    override reset(): void {
        console.log(`${InteractionService.SERVICE_NAME} reset called`);
        this.disposeAll();
        super.reset();
    }

    /**
     * Register default keyboard shortcuts.
     */
    private registerDefaultShortcuts(): void {
        this.keyboardManager.registerShortcut({
            key: 'm',
            description: 'Activate Distance Measurement Tool',
            callback: (e) => {
                console.log(
                    '[InteractionService] Shortcut M pressed - distance measurement not yet wired to ToolGroups, trigger by :\n',
                    e
                );
                this.setPrimaryTool(e.viewport.id, DistanceMeasurementTool.toolName);
            },
        });

        this.broadcast(this.EVENTS.SHORTCUT_REGISTERED, {
            shortcuts: this.keyboardManager.getShortcuts(),
        });
    }
}
